import { useState, useEffect, useRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { AVAILABLE_MODELS, DEFAULT_MODEL_ID } from "../shared/chat-config";
import { DEFAULT_PERSONA_NAME } from "../brain/persona";
import { getStoredToken } from "../hooks/useAuth";

const DEFAULT_MODEL_LABEL = AVAILABLE_MODELS.find(m => m.id === DEFAULT_MODEL_ID)?.label ?? "Unknown";

interface BrainStripProps {
  connected: boolean;
  modelLabel?: string;
  /** PATH B SLICE 4 — the active session's companion display name, sourced
   *  from the server's CompanionDescriptor once the init frame arrives.
   *  Falls back to persona.ts's build-time constant for the cold-render window. */
  companionName?: string;
}

// Usage Meters Slice 2 fix (2026-08-07): the server route now exists but is a
// deliberate stub (no cheap gateway-side cache of live brain state — see
// server.ts's /api/brain/status comment) — every field is nullable and the
// UI must render "—" per-field, not assume a non-null BrainStatus means every
// field is populated.
interface BrainStatus {
  mood: string | null;
  energy: number | null;
  momentum: string | null;
  memory_count: number | null;
  last_activity: string | null;
  open_loops_count: number | null;
}

// ─── Usage Meters Slice 1 (Eli's design, 2026-08-05) ──────────────────────────
// Mirrors src/gateway/usage-limits-store.ts's UsageLimitRow shape.

type UsageProvider = "claude" | "codex";

interface UsageLimitRow {
  provider: UsageProvider;
  bucket_id: string;
  window_label: string | null;
  used_percent: number | null;
  resets_at: number | null;
  status: string;
  balance: string | null;
  observed_at: number;
  source: string;
}

type UsageTier = "unknown" | "low" | "mid" | "high" | "exhausted";

const HIDE_USAGE_STORAGE_KEY = "muse-hide-usage-limits";
const TIER_RANK: Record<UsageTier, number> = { unknown: 0, low: 1, mid: 2, high: 3, exhausted: 4 };

function classifyUsageRow(row: UsageLimitRow | undefined): UsageTier {
  if (!row) return "unknown";
  if (row.status === "rejected" || row.status === "exhausted") return "exhausted";
  if (row.used_percent == null) return "unknown";
  if (row.used_percent >= 80) return "high";
  if (row.used_percent >= 60) return "mid";
  return "low";
}

// The "tightest" bucket = the one in the worst state — this is what a silent
// strip should surface first. Ties within the same tier break on used_percent.
function pickTightest(rows: UsageLimitRow[]): UsageLimitRow | undefined {
  return rows.reduce<UsageLimitRow | undefined>((best, row) => {
    if (!best) return row;
    const bestRank = TIER_RANK[classifyUsageRow(best)];
    const rowRank = TIER_RANK[classifyUsageRow(row)];
    if (rowRank !== bestRank) return rowRank > bestRank ? row : best;
    return (row.used_percent ?? -1) > (best.used_percent ?? -1) ? row : best;
  }, undefined);
}

function providerLabel(provider: UsageProvider): string {
  return provider === "claude" ? "Claude" : "Codex";
}

function formatRelativeDuration(deltaMs: number): string {
  if (deltaMs <= 0) return "now";
  const totalMinutes = Math.round(deltaMs / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}

function formatBerlinAbsolute(ms: number): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("weekday")} ${get("day")} ${get("month")} ${get("hour")}:${get("minute")}`;
}

// Absolute (Berlin local) AND relative reset time, per the locked product
// decision — e.g. "Sat 8 Aug 20:10 (in 2d 21h)".
export function formatResetBerlin(resetsAtSeconds: number, now: number = Date.now()): string {
  const resetMs = resetsAtSeconds * 1000;
  return `${formatBerlinAbsolute(resetMs)} (${formatRelativeDuration(resetMs - now)})`;
}

// ─── Staleness (Usage Meters Slice 2, Eli's design 2026-08-05) ───────────────
// A bucket's own observed_at is the gateway's last write for it — never
// present it as live once it's gone quiet. ~2h threshold, per spec.
const STALE_THRESHOLD_MS = 2 * 60 * 60 * 1000;

function isStaleRow(row: UsageLimitRow, now: number = Date.now()): boolean {
  return now - row.observed_at > STALE_THRESHOLD_MS;
}

function staleTitle(row: UsageLimitRow): string {
  return `as of ${formatBerlinAbsolute(row.observed_at)}`;
}

function usageSummaryText(row: UsageLimitRow | undefined): string {
  if (!row) return "—";
  const tier = classifyUsageRow(row);
  if (tier === "exhausted") {
    const reset = row.resets_at != null ? formatResetBerlin(row.resets_at) : "reset time unknown";
    return `${providerLabel(row.provider)} out · back ${reset}`;
  }
  if (row.used_percent == null) return "—";
  const label = row.window_label ?? `${providerLabel(row.provider)} ${row.bucket_id}`;
  return `${label} ${row.used_percent}%`;
}

// One line of detail for the expanded popover (Usage Meters Slice 2):
// window label, percent OR credits/balance state, reset time — all a bucket
// can report.
function bucketDetailText(row: UsageLimitRow): string {
  const label = row.window_label ?? `${providerLabel(row.provider)} ${row.bucket_id}`;
  const tier = classifyUsageRow(row);
  if (tier === "exhausted") {
    const reset = row.resets_at != null ? formatResetBerlin(row.resets_at) : "reset time unknown";
    return `${label} — out · back ${reset}`;
  }
  if (row.balance != null) {
    return `${label} — balance ${row.balance}`;
  }
  if (row.used_percent != null) {
    const resetSuffix = row.resets_at != null ? ` · resets ${formatResetBerlin(row.resets_at)}` : "";
    return `${label} — ${row.used_percent}%${resetSuffix}`;
  }
  return `${label} — —`;
}

function readHideUsagePref(): boolean {
  try {
    return localStorage.getItem(HIDE_USAGE_STORAGE_KEY) === "1";
  } catch {
    // localStorage unavailable (private browsing, quota) — default to shown.
    return false;
  }
}

function writeHideUsagePref(hidden: boolean): void {
  try {
    localStorage.setItem(HIDE_USAGE_STORAGE_KEY, hidden ? "1" : "0");
  } catch {
    // Best-effort — the in-memory toggle still works for this session.
  }
}

export function BrainStrip({ connected, modelLabel, companionName = DEFAULT_PERSONA_NAME }: BrainStripProps) {
  const [brain, setBrain] = useState<BrainStatus | null>(null);
  const [usage, setUsage] = useState<UsageLimitRow[] | null>(null);
  const [hideUsage, setHideUsage] = useState<boolean>(readHideUsagePref);
  // Usage Meters Slice 2 — bucket detail popover ("click Limits → all buckets").
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchStatus() {
      // Usage Meters Slice 2 fix (2026-08-07): every other /api/ route
      // requires this header — the pre-existing fetch here omitted it, which
      // combined with the previously-missing server route meant this call
      // never worked in production. Same pattern as fetchUsage() below.
      const token = getStoredToken();
      if (!token) return;
      try {
        const res = await fetch("/api/brain/status", {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const data = await res.json() as BrainStatus;
          setBrain(data);
        }
      } catch {
        // Non-critical — BrainStrip degrades gracefully if the fetch fails
      }
    }

    fetchStatus(); // Initial fetch
    const interval = setInterval(fetchStatus, 30000); // Poll every 30s to stay current after mind_store calls

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // SILENT by product decision — no toast/nudge on fetch failure, degrade to "—".
  useEffect(() => {
    let cancelled = false;

    async function fetchUsage() {
      const token = getStoredToken();
      if (!token) return;
      try {
        const res = await fetch("/api/usage", {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const data = await res.json() as { limits: UsageLimitRow[] };
          setUsage(data.limits ?? []);
        }
      } catch {
        // Non-critical — degrades to "—", never a toast/nudge.
      }
    }

    fetchUsage();
    const interval = setInterval(fetchUsage, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Close the popover on outside click or Escape — mirrors InputControls.tsx's
  // established popover pattern.
  useEffect(() => {
    if (!popoverOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setPopoverOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setPopoverOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [popoverOpen]);

  function toggleHideUsage() {
    setHideUsage((prev) => {
      const next = !prev;
      writeHideUsagePref(next);
      return next;
    });
  }

  const claudeSession = usage?.find((r) => r.provider === "claude" && r.bucket_id === "five_hour");
  const claudeWeekly = usage
    ? pickTightest(usage.filter((r) => r.provider === "claude" && r.bucket_id.startsWith("seven_day")))
    : undefined;
  // Renamed from codexWeekly (Fischer's nit, 2026-08-05 review): this is the
  // tightest codex bucket regardless of window — codex's primary window can
  // be 5h/daily/weekly depending on plan, "weekly" was never guaranteed true.
  const codexTightest = usage ? pickTightest(usage.filter((r) => r.provider === "codex")) : undefined;

  const bars = [
    { key: "claude-session", label: "Claude 5h", row: claudeSession },
    { key: "claude-weekly", label: "Claude 7d", row: claudeWeekly },
    { key: "codex-tightest", label: "Codex", row: codexTightest },
  ].filter((bar): bar is { key: string; label: string; row: UsageLimitRow } => bar.row !== undefined);

  const tightestOverall = usage ? pickTightest(usage) : undefined;
  const summaryTier = classifyUsageRow(tightestOverall);
  const summaryText = usageSummaryText(tightestOverall);
  const summaryStale = tightestOverall != null && isStaleRow(tightestOverall);

  return (
    <div className="brain-strip">
      <div className="brain-item">
        <span className={`status-dot ${connected ? "connected" : "disconnected"}`} />
        {/* WCAG 4.1.3 — companionName is runtime-dynamic (switches with the
            active session's companion); announce the change to AT. */}
        <span className="brain-value" role="status" aria-live="polite">{companionName}</span>
      </div>
      <div className="brain-item">
        <span className="brain-label">Model</span>
        <span className="brain-value">{modelLabel ?? DEFAULT_MODEL_LABEL}</span>
      </div>
      <div className="brain-item">
        <span className="brain-label">Memories</span>
        <span className="brain-value">{brain?.memory_count != null ? brain.memory_count.toString() : "—"}</span>
      </div>
      <div className="brain-item">
        <span className="brain-label">Mood</span>
        <span className="brain-value">{brain?.mood ?? "—"}</span>
      </div>
      {!hideUsage && (
        <div className="brain-item usage-item">
          <span className="brain-label">Limits</span>
          {bars.length === 0 ? (
            <span className="brain-value usage-summary usage-summary--unknown">—</span>
          ) : (
            <>
              <button
                type="button"
                ref={triggerRef}
                className="usage-limits-trigger"
                onClick={() => setPopoverOpen((prev) => !prev)}
                aria-expanded={popoverOpen}
                aria-haspopup="true"
                aria-label={`Usage limits detail: ${summaryText}`}
              >
                <span className="usage-bars" aria-hidden="true">
                  {bars.map(({ key, label, row }) => {
                    const tier = classifyUsageRow(row);
                    const pct = row.used_percent ?? (tier === "exhausted" ? 100 : 0);
                    const stale = isStaleRow(row);
                    return (
                      <span
                        key={key}
                        className={`usage-bar usage-bar--${tier}${stale ? " usage-bar--stale" : ""}`}
                        title={`${label}: ${usageSummaryText(row)}${stale ? ` (${staleTitle(row)})` : ""}`}
                      >
                        <span className="usage-bar-fill" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
                        {tier === "high" && <span className="usage-dot-pulse" aria-hidden="true" />}
                      </span>
                    );
                  })}
                </span>
                <span
                  className={`brain-value usage-summary usage-summary--${summaryTier}${summaryStale ? " usage-summary--stale" : ""}`}
                  title={summaryStale && tightestOverall ? staleTitle(tightestOverall) : undefined}
                >
                  {summaryText}
                </span>
              </button>
              {/* Non-interactive disclosure — deliberately NOT role="dialog": this
                  codebase pairs that role with aria-modal + focus-trap + focus-restore
                  (ModelSwitchConfirmDialog), which static content doesn't warrant. */}
              {popoverOpen && usage && (
                <div className="usage-popover" ref={popoverRef} role="group" aria-label="Usage limits detail">
                  {usage.map((row) => {
                    const stale = isStaleRow(row);
                    return (
                      <div
                        key={`${row.provider}-${row.bucket_id}`}
                        className={`usage-popover-row${stale ? " usage-popover-row--stale" : ""}`}
                        title={stale ? staleTitle(row) : undefined}
                      >
                        {bucketDetailText(row)}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
      <button
        type="button"
        className="usage-toggle"
        onClick={toggleHideUsage}
        aria-pressed={hideUsage}
        aria-label={hideUsage ? "Show usage limits" : "Hide usage limits"}
        title={hideUsage ? "Show usage limits" : "Hide usage limits"}
      >
        {hideUsage ? <EyeOff size={12} /> : <Eye size={12} />}
      </button>
    </div>
  );
}
